import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "utils/dayjs";
import { Alert, Typography } from "@mui/material";
import { BarChart } from "@outpave/ds";
import type { BarSeriesType } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import EarnRulesTable from "./EarnRulesTable";
import type { PlatformRewardRule, RewardRuleStatus } from "./types";
import {
  REWARD_RULE_STATUS_LABEL,
  promotionOverlaps,
} from "utils/rewards/rewardRules";

const LANE_HEIGHT = 48;
const AXIS_HEIGHT = 72;
const LANE_STATUSES: RewardRuleStatus[] = [
  "LIVE",
  "SCHEDULED",
  "ENDED",
  "PAUSED",
];

interface PromotionsTimelineProps {
  rules: PlatformRewardRule[];
  loading: boolean;
  error: boolean;
  onOpen: (rule: PlatformRewardRule) => void;
}

const PromotionsTimeline: React.FC<PromotionsTimelineProps> = ({
  rules,
  loading,
  error,
  onOpen,
}) => {
  const { t } = useTranslation();
  const promotions = useMemo(
    () =>
      rules
        .filter((rule) => rule.startsOn && rule.endsOn)
        .sort((a, b) =>
          (a.startsOn as string).localeCompare(b.startsOn as string),
        ),
    [rules],
  );
  const overlaps = useMemo(() => promotionOverlaps(rules), [rules]);

  const chart = useMemo(() => {
    if (!promotions.length) return null;
    const today = dayjs.utc().startOf("day");
    const earliest = dayjs.utc(promotions[0].startsOn as string);
    const latest = promotions.reduce((max, rule) => {
      const end = dayjs.utc(rule.endsOn as string);
      return end.isAfter(max) ? end : max;
    }, today);
    const from = (earliest.isBefore(today) ? earliest : today).startOf("month");
    const to = latest.endOf("month");
    const offset = (day: string) => dayjs.utc(day).diff(from, "day");
    const series: BarSeriesType[] = [
      {
        id: "base",
        data: promotions.map((rule) => offset(rule.startsOn as string)),
        stack: "window",
        color: "transparent",
        valueFormatter: () => null,
      },
      ...LANE_STATUSES.filter((status) =>
        promotions.some((rule) => rule.status === status),
      ).map((status) => ({
        id: status,
        label: t(REWARD_RULE_STATUS_LABEL[status]),
        stack: "window",
        data: promotions.map((rule) =>
          rule.status === status
            ? offset(rule.endsOn as string) -
              offset(rule.startsOn as string) +
              1
            : 0,
        ),
        valueFormatter: (
          _value: number,
          { dataIndex }: { dataIndex: number },
        ) =>
          promotions[dataIndex].status === status
            ? `${promotions[dataIndex].ratePercent}%`
            : null,
      })),
    ];
    return {
      series,
      max: to.diff(from, "day") + 1,
      today: today.diff(from, "day"),
      label: (day: number) => from.add(day, "day").format("MMM D"),
    };
  }, [promotions, t]);

  return (
    <Stack gap="var(--space-5)" sx={{ padding: "var(--space-5)" }}>
      {chart && (
        <BarChart
          layout="horizontal"
          height={promotions.length * LANE_HEIGHT + AXIS_HEIGHT}
          series={chart.series}
          yAxis={[
            { scaleType: "band", data: promotions.map((rule) => rule.name) },
          ]}
          xAxis={[
            {
              scaleType: "linear",
              min: 0,
              max: chart.max,
              valueFormatter: chart.label,
            },
          ]}
          referenceLines={[{ x: chart.today, label: t("Today") }]}
          onItemClick={(_event, item) => onOpen(promotions[item.dataIndex])}
        />
      )}
      {overlaps.map(({ first, second, winner }) => (
        <Alert key={`${first.id}-${second.id}`} severity="info">
          {t("PromotionOverlap", {
            first: first.name,
            second: second.name,
            winner: winner.name,
            priority: winner.priority,
          })}
        </Alert>
      ))}
      <EarnRulesTable
        rules={promotions}
        loading={loading}
        error={error}
        emptyState={t("NoPromotions")}
        onOpen={onOpen}
      />
      {chart && (
        <Typography variant="caption" color="text.secondary">
          {t("PromotionsTimelineCaption")}
        </Typography>
      )}
    </Stack>
  );
};

export default PromotionsTimeline;
